/**
 * Sistema simple para manejar tokens OAuth en localStorage
 */

/**
 * Guardar tokens de un proveedor
 */
export const saveTokens = (providerId, tokenData) => {
  try {
    if (!tokenData.expires_in) {
      console.error(`❌ ${providerId} no envió expires_in en los tokens`);
      return false;
    }
    
    const expiresAt = Date.now() + (tokenData.expires_in * 1000);

    const tokenInfo = {
      accessToken: tokenData.access_token,
      refreshToken: tokenData.refresh_token,
      tokenType: tokenData.token_type || 'Bearer',
      expiresAt,
      scope: tokenData.scope,
      provider: tokenData.provider,
      userInfo: tokenData.userInfo || {},
      savedAt: Date.now()
    };

    localStorage.setItem(`oauth_${providerId}`, JSON.stringify(tokenInfo));

    return true;
  } catch (error) {
    console.error(`❌ Error guardando tokens de ${providerId}:`, error);
    return false;
  }
};

/**
 * Obtener tokens de un proveedor
 */
export const getTokens = (providerId) => {
  try {
    const tokenData = localStorage.getItem(`oauth_${providerId}`);
    if (!tokenData) return null;
    
    const tokens = JSON.parse(tokenData);
    
    // Verificar si han expirado
    if (tokens.expiresAt && Date.now() > tokens.expiresAt) {
      const expiredDate = new Date(tokens.expiresAt);
      localStorage.removeItem(`oauth_${providerId}`);
      return null;
    }
    
    return tokens;
  } catch (error) {
    console.error(`❌ Error obteniendo tokens de ${providerId}:`, error);
    return null;
  }
};

/**
 * Verificar si hay tokens válidos
 */
export const hasValidTokens = (providerId) => {
  return getTokens(providerId) !== null;
};

/**
 * Obtener access token
 */
export const getAccessToken = (providerId) => {
  const tokens = getTokens(providerId);
  return tokens ? tokens.accessToken : null;
};

/**
 * Refrescar tokens usando refresh_token
 */
export const refreshTokens = async (providerId) => {
  const tokens = getTokens(providerId);
  if (!tokens || !tokens.refreshToken) {
    console.error(`❌ No hay refresh token para ${providerId}`);
    return false;
  }

  try {
    const apiUrl = process.env.REACT_APP_API_URL;
    const response = await fetch(`${apiUrl}/oauth/${providerId}/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        provider: providerId,
        refresh_token: tokens.refreshToken
      })
    });

    if (!response.ok) {
      throw new Error(`Error del servidor: ${response.status} - ${response.statusText}`);
    }

    const newTokenData = await response.json();
    
    // Guardar nuevos tokens
    const saved = saveTokens(providerId, newTokenData);
    if (saved) {
      return true;
    } else {
      throw new Error('Error guardando tokens refrescados');
    }

  } catch (error) {
    console.error(`❌ Error refrescando tokens de ${providerId}:`, error);
    
    // Si falló el refresh, limpiar tokens expirados
    clearTokens(providerId);
    return false;
  }
};

/**
 * Obtener access token válido (con refresh automático si es necesario)
 */
export const getValidAccessToken = async (providerId) => {
  const tokens = getTokens(providerId);
  if (!tokens) {
    return null;
  }

  // Si los tokens están cerca de expirar (menos de 5 minutos), intentar refresh
  const timeUntilExpiry = tokens.expiresAt - Date.now();
  const fiveMinutes = 5 * 60 * 1000; // 5 minutos en milisegundos
  
  if (timeUntilExpiry < fiveMinutes) {
    const refreshed = await refreshTokens(providerId);
    
    if (refreshed) {
      // Obtener tokens actualizados después del refresh
      const updatedTokens = getTokens(providerId);
      return updatedTokens ? updatedTokens.accessToken : null;
    } else {
      return null;
    }
  }

  return tokens.accessToken;
};

/**
 * Limpiar tokens
 */
export const clearTokens = (providerId) => {
  localStorage.removeItem(`oauth_${providerId}`);
};

/**
 * Hacer petición con autorización automática y refresh si es necesario
 */
export const authorizedFetch = async (providerId, url, options = {}) => {
  try {
    const accessToken = await getValidAccessToken(providerId);
    if (!accessToken) {
      throw new Error(`No hay token válido para ${providerId}`);
    }

    const headers = {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      ...options.headers
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    // Si es 401, los tokens pueden haber expirado - intentar refresh una vez más
    if (response.status === 401) {
      
      const refreshed = await refreshTokens(providerId);
      if (refreshed) {
        // Reintentar con token renovado
        const newAccessToken = await getValidAccessToken(providerId);
        if (newAccessToken) {
          const newHeaders = {
            'Authorization': `Bearer ${newAccessToken}`,
            'Content-Type': 'application/json',
            ...options.headers
          };

          return await fetch(url, {
            ...options,
            headers: newHeaders
          });
        }
      }
      
      throw new Error(`Token expirado y no se pudo refrescar para ${providerId}`);
    }

    return response;
  } catch (error) {
    console.error(`❌ Error en petición autorizada a ${providerId}:`, error);
    throw error;
  }
};

